import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useBlocker, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { GET_ME, GET_SESSION_EXERCISES, GET_WORKOUTS, GET_WORKOUT_LOGS } from '../graphql/queries';
import { lockedWorkoutIds } from '../utils/workoutLock';
import { CREATE_WORKOUT_LOG, UPDATE_WORKOUT } from '../graphql/mutations';
import { trackPageView, trackEvent } from '../utils/gt';
import { speak, cancelSpeech, prepareSpeech, setSpeechVolume } from '../utils/speech';
import { loadVoiceLevel, saveVoiceLevel, voiceGain } from '../utils/voiceVolume';
import { speakablePt } from '../utils/speechText';
import { isAppleMobile } from '../utils/device';
import { canUseVoice, isVoiceReady, prepareLine, prepareVoice, unlockVoiceAudio, voiceDownloadBytes } from '../utils/voice';
import { useWakeLock } from '../utils/useWakeLock';
import { findNewRecords } from '../utils/records';
import { estimateWorkoutMinutes } from '../utils/workoutStats';
import { carryLoad, entryKind, formatNumber, howToSteps, loadStep, normalizeLoad, stepLoad } from '../utils/guidedSession';

import { LeaveSessionDialog, SessionActive, SessionComplete, SessionIntro } from '../components/session/SessionView';

// The load as said aloud: "10 quilos", never "10kg" (a voice reads that as "dez ká gê")
const spokenLoad = (weight, unit, isPt) => {
  const isLbs = unit === 'lbs';
  const one = Number(weight) === 1;
  if (isPt) return `${String(weight).replace('.', ',')} ${isLbs ? (one ? 'libra' : 'libras') : one ? 'quilo' : 'quilos'}`;
  return `${weight} ${isLbs ? (one ? 'pound' : 'pounds') : one ? 'kilogram' : 'kilograms'}`;
};

const COMPLETE_SPEECH = { pt: 'Parabéns! Você completou o treino!', en: 'Great job! Workout complete!' };

// Warm-up and feeder entries are saved as "Supino (Aquecimento)" / "Supino (Preparo)" (or just flagged warmup). Read
// as written, the voice said "supino preparo": they're said as what they are, "série de preparo para Supino".
const SET_KIND_WORD = { pt: { warmup: 'aquecimento', feeder: 'preparo' }, en: { warmup: 'warm-up', feeder: 'feeder' } };
const spokenEntry = entryKind;

// Several warm-up / feeder entries in a row for one lift (each with its own load and reps, as low-volume workouts
// ramp up) are rounds: "a segunda rodada de preparo para Supino"
const ORDINAL = {
  pt: ['primeira', 'segunda', 'terceira', 'quarta', 'quinta', 'sexta', 'sétima', 'oitava', 'nona', 'décima'],
  en: ['first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth'],
};
const ordinal = (n, isPt) => ORDINAL[isPt ? 'pt' : 'en'][n - 1] || (isPt ? `${n}ª` : `${n}th`);

const buildWarmupSpeech = ({ base, kind, round, rounds, setNumber, totalSets, repsStr, weightLine, howToLine, isFirstEverSet, isPt }) => {
  const word = SET_KIND_WORD[isPt ? 'pt' : 'en'][kind];
  const plural = totalSets > 1;
  const nth = rounds > 1 ? ordinal(round, isPt) : null;
  if (isPt) {
    const what = nth ? `a ${nth} rodada de ${word} para ${base}` : `${plural ? 'as séries' : 'a série'} de ${word} para ${base}`;
    const dose = `${totalSets} ${plural ? 'séries' : 'série'} de ${repsStr} ${repsStr === '1' ? 'repetição' : 'repetições'}${weightLine}`;
    if (isFirstEverSet) return `Vamos começar com ${what}: ${dose}.${howToLine}`;
    if (setNumber === 1) return `Fim do intervalo. Agora, ${what}: ${dose}.${howToLine}`;
    return nth
      ? `Fim do intervalo. Continue para a série ${setNumber} da ${nth} rodada de ${word} para ${base}.`
      : `Fim do intervalo. Continue para a série ${setNumber} de ${word} para ${base}.`;
  }
  const what = nth ? `the ${nth} ${word} round for ${base}` : `the ${word} ${plural ? 'sets' : 'set'} for ${base}`;
  const dose = `${totalSets} ${plural ? 'sets' : 'set'} of ${repsStr} ${repsStr === '1' ? 'repetition' : 'repetitions'}${weightLine}`;
  if (isFirstEverSet) return `Let's start with ${what}: ${dose}.${howToLine}`;
  if (setNumber === 1) return `End of rest. Next, ${what}: ${dose}.${howToLine}`;
  return nth ? `End of rest. Proceed to set ${setNumber} of the ${nth} ${word} round for ${base}.` : `End of rest. Proceed to ${word} set ${setNumber} for ${base}.`;
};

const buildExerciseSpeech = ({ currentSet, currentSetIdx, isLastSet, isPt, voiceInstructions }) => {
  const { exercise, setNumber, totalSets, isFirstOccurrence, round, rounds } = currentSet;
  const { name, reps, howTo, weight, weightUnit } = exercise;
  const isFirstEverSet = currentSetIdx === 0;
  const isNewExercise = setNumber === 1;

  const repsStr = String(reps);
  const howToLine = howTo && isFirstOccurrence && voiceInstructions !== false ? ` ${howTo}` : '';
  const weightLine = weight ? (isPt ? ` com ${spokenLoad(weight, weightUnit, isPt)}` : ` with ${spokenLoad(weight, weightUnit, isPt)}`) : '';

  const { base, kind } = spokenEntry(exercise);
  if (kind) {
    return buildWarmupSpeech({ base, kind, round, rounds, setNumber, totalSets, repsStr, weightLine, howToLine, isFirstEverSet, isPt });
  }

  if (isFirstEverSet) {
    return isPt
      ? `Seu primeiro exercício é ${name}. Você fará ${totalSets} ${totalSets === 1 ? 'série' : 'séries'} de ${repsStr} ${repsStr === '1' ? 'repetição' : 'repetições'}${weightLine}.${howToLine}`
      : `Your first exercise is ${name}. You will do ${totalSets} ${totalSets === 1 ? 'set' : 'sets'} of ${repsStr} ${repsStr === '1' ? 'repetition' : 'repetitions'}${weightLine}.${howToLine}`;
  }

  if (isLastSet && isNewExercise) {
    return isPt
      ? `Fim do intervalo. Último exercício: ${name}. Faça ${repsStr} repetições${weightLine} e o seu treino estará terminado!${howToLine}`
      : `End of rest. Last exercise: ${name}. Do ${repsStr} repetitions${weightLine} and your workout will be complete!${howToLine}`;
  }

  if (isLastSet) {
    return isPt
      ? `Fim do intervalo. Faça as últimas ${repsStr} repetições de ${name} e o seu treino estará terminado!`
      : `End of rest. Do the last ${repsStr} repetitions of ${name} and your workout will be complete!`;
  }

  if (isNewExercise) {
    return isPt
      ? `Fim do intervalo. Vamos para a primeira série de ${name}. Você fará ${totalSets} ${totalSets === 1 ? 'série' : 'séries'} de ${repsStr} repetições${weightLine}.${howToLine}`
      : `End of rest. Let's move on to the first set of ${name}. You will do ${totalSets} ${totalSets === 1 ? 'set' : 'sets'} of ${repsStr} repetitions${weightLine}.${howToLine}`;
  }

  return isPt
    ? `Fim do intervalo. Continue para a série ${setNumber} de ${name}.`
    : `End of rest. Proceed to set ${setNumber} of ${name}.`;
};

export const WorkoutExecution = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { workoutId } = useParams();
  const navigate = useNavigate();
  // The page that started the workout (Início, Meus treinos), for the events its start choice sends
  const startSource = useLocation().state?.from;

  const [phase, setPhase] = useState('intro'); // 'intro' | 'exercise' | 'rest' | 'complete'
  const [currentSetIdx, setCurrentSetIdx] = useState(0);
  // The rest is timed against the clock, not by counting ticks: a backgrounded tab can't stall it
  const [restEndsAt, setRestEndsAt] = useState(null);
  const [restTotal, setRestTotal] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [isMuted, setIsMuted] = useState(false);
  const [voiceLevel, setVoiceLevel] = useState(loadVoiceLevel);
  const volumePreviewRef = useRef(null);
  // The workout's own voice (pt, src/utils/voice), before the first set:
  // 'idle' | 'confirm' (asking before a download) | 'downloading' | 'preparing'
  const [voiceStatus, setVoiceStatus] = useState('idle');
  const [voiceProgress, setVoiceProgress] = useState(null);
  const [voiceDownloadSize, setVoiceDownloadSize] = useState(0);
  const voiceAbortRef = useRef(null);
  // The tip for the coming rest, picked during the set so its line can be made ahead
  const nextTipRef = useRef(null);
  const [logStatus, setLogStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'pro' | 'error'
  // Per-set overrides keyed by `${exerciseIdx}-${setNumber}` → { reps, weight, weightUnit, weightTyped }
  // (weightTyped: the load was changed on that set by hand, so a change on an earlier set doesn't overwrite it)
  const [setOverrides, setSetOverrides] = useState({});
  const startTimeRef = useRef(null);
  const [durationSeconds, setDurationSeconds] = useState(0);

  const { data: dataWorkouts, loading: isLoadingWorkouts, refetch: refetchWorkouts } = useQuery(GET_WORKOUTS);
  const { data: dataMe } = useQuery(GET_ME);
  const [createWorkoutLogMutation] = useMutation(CREATE_WORKOUT_LOG);
  const [updateWorkoutMutation] = useMutation(UPDATE_WORKOUT);
  const workouts = dataWorkouts?.workouts?.data;
  const workout = workouts?.find((w) => w.id === workoutId);
  const workoutData = workout?.attributes;
  const me = dataMe?.me;
  const isPro = me?.plan === 'pro' && ['active', 'trialing'].includes(me?.subscriptionStatus);
  // Earlier sessions, to tell a new record at the end (history is Pro, so free accounts skip it)
  const { data: dataLogs } = useQuery(GET_WORKOUT_LOGS, { skip: !isPro });
  const [newRecords, setNewRecords] = useState([]);
  // The library's word on each exercise: the muscles it works and its equipment (the step its load moves by)
  const libraryIds = useMemo(
    () => [...new Set((workoutData?.exercises || []).map((exercise) => exercise.exerciseId).filter(Boolean))],
    [workoutData]
  );
  const { data: dataLibrary } = useQuery(GET_SESSION_EXERCISES, { variables: { ids: libraryIds }, skip: !libraryIds.length });

  const isPt = language === 'pt';
  const langCode = isPt ? 'pt-BR' : 'en-US';
  const isInProgress = phase === 'exercise' || phase === 'rest';

  useWakeLock(isInProgress);

  // Leaving mid-session (close button, back button, any link) asks first
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => isInProgress && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (!isInProgress) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isInProgress]);

  const getSetKey = (exerciseIdx, setNumber) => `${exerciseIdx}-${setNumber}`;

  const getSetValue = (exerciseIdx, setNumber, field, fallback) => {
    const key = getSetKey(exerciseIdx, setNumber);
    if (setOverrides[key] && setOverrides[key][field] !== undefined) return setOverrides[key][field];
    return fallback;
  };

  const updateSetOverride = (exerciseIdx, setNumber, field, value) => {
    const key = getSetKey(exerciseIdx, setNumber);
    setSetOverrides((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value, ...(field === 'weight' && { weightTyped: true }) },
    }));
  };

  // The load of a set carries to the later sets of the exercise not changed by hand: on blur when typed (the whole
  // number, not each keystroke), at once from the steppers
  const propagateWeightForward = (exerciseIdx, setNumber, value) => {
    const seriesCount = parseInt(workoutData?.exercises?.[exerciseIdx]?.series) || 1;
    setSetOverrides((prev) => carryLoad(prev, exerciseIdx, setNumber, seriesCount, value));
  };

  useEffect(() => {
    trackPageView('workout_execution', `${t('appUrl')}/execute/${workoutId}`, '/execute');
  }, [t, workoutId]);

  useEffect(() => {
    if (!isLoadingWorkouts && workouts && !workout) {
      navigate('/workouts');
    }
  }, [isLoadingWorkouts, workouts, workout, navigate]);

  // A workout kept locked (Pro ended) isn't trained: back to the list, which says why
  const isLockedWorkout = lockedWorkoutIds(workouts, me).has(workoutId);
  useEffect(() => {
    if (!isLockedWorkout) return;
    toast.info(t('notifications.workoutLocked'), { toastId: 'workout-locked' });
    navigate('/workouts', { replace: true });
  }, [isLockedWorkout, navigate, t]);

  // Cancel speech when leaving the page
  useEffect(() => {
    return () => cancelSpeech();
  }, []);

  const allSets = useMemo(() => {
    if (!workoutData?.exercises) return [];
    const sets = [];
    const seenBaseNames = new Set();
    // Consecutive warm-up / feeder entries of the same lift: which round each one is, and how many there are
    const entries = workoutData.exercises.map(spokenEntry);
    const sameKind = (a, b) => !!a && !!b && !!a.kind && a.kind === b.kind && a.base === b.base;
    const roundOf = (i) => {
      let start = i;
      while (sameKind(entries[start - 1], entries[i])) start -= 1;
      let end = i;
      while (sameKind(entries[end + 1], entries[i])) end += 1;
      return { round: i - start + 1, rounds: end - start + 1 };
    };
    workoutData.exercises.forEach((exercise, exerciseIdx) => {
      const { round, rounds } = roundOf(exerciseIdx);
      const baseName = exercise.name
        .replace(/\s*\(warmup\)/gi, '')
        .replace(/\s*\(aquecimento\)/gi, '')
        .replace(/\s*\(feeder\)/gi, '')
        .replace(/\s*\(preparo\)/gi, '')
        .trim();
      const isFirstOccurrence = !seenBaseNames.has(baseName);
      if (isFirstOccurrence) seenBaseNames.add(baseName);
      const seriesCount = parseInt(exercise.series) || 1;
      for (let i = 0; i < seriesCount; i++) {
        sets.push({
          exerciseIdx,
          setNumber: i + 1,
          totalSets: seriesCount,
          exercise,
          isFirstOccurrence,
          round,
          rounds,
        });
      }
    });
    return sets;
  }, [workoutData]);

  // Each exercise as the screen shows it: the lift's name with its kind (warm-up, feeder) apart, the muscles from the
  // library, the how-to as steps, the dose and the step of its load
  const entries = useMemo(() => {
    const library = new Map((dataLibrary?.exercises?.data || []).map(({ attributes }) => [attributes.exerciseId, attributes]));
    const muscle = (key) => t(`exerciseFilters.muscleGroups.${key}`);
    const list = (items) =>
      items.length > 1 ? `${items.slice(0, -1).join(', ')} ${t('pages.WorkoutExecution.session.and')} ${items[items.length - 1]}` : items[0];
    return (workoutData?.exercises || []).map((exercise, index) => {
      const { base, kind } = entryKind(exercise);
      const info = library.get(exercise.exerciseId);
      const main = info?.muscleGroup ? muscle(info.muscleGroup) : null;
      const others = (info?.otherMuscles || []).filter((key) => key !== info.muscleGroup).map((key) => muscle(key).toLowerCase());
      const unit = exercise.weightUnit || 'kg';
      return {
        index,
        exercise,
        name: base,
        kind,
        unit,
        totalSets: parseInt(exercise.series) || 1,
        muscle: main,
        works: main && others.length ? t('pages.WorkoutExecution.session.worksAlso', { muscle: main, others: list(others) }) : main,
        steps: howToSteps(exercise.howTo),
        loadStep: loadStep(info?.equipmentCategory, base, unit),
        dose: `${exercise.series} × ${exercise.reps}${exercise.weight ? ` · ${formatNumber(exercise.weight, isPt)} ${unit}` : ''}`,
      };
    });
  }, [workoutData, dataLibrary, t, isPt]);

  const currentSet = allSets[currentSetIdx];
  const nextSet = allSets[currentSetIdx + 1];
  const isLastSet = currentSetIdx === allSets.length - 1;
  // A set counts as done the moment it's completed, not when the rest after it ends
  const completedSetsCount = phase === 'complete' ? allSets.length : phase === 'rest' ? currentSetIdx + 1 : currentSetIdx;
  const restTimeLeft = phase === 'rest' && restEndsAt ? Math.max(0, Math.ceil((restEndsAt - now) / 1000)) : 0;

  // Duration is fixed in the same update that ends the session, so the log saves it
  const finishSession = useCallback(() => {
    setDurationSeconds(startTimeRef.current ? Math.round((Date.now() - startTimeRef.current) / 1000) : 0);
    setPhase('complete');
  }, []);

  const advanceAfterRest = useCallback(() => {
    const nextIdx = currentSetIdx + 1;
    setRestEndsAt(null);
    if (nextIdx >= allSets.length) {
      finishSession();
    } else {
      setCurrentSetIdx(nextIdx);
      setPhase('exercise');
    }
  }, [currentSetIdx, allSets.length, finishSession]);

  // Clock for the rest countdown
  useEffect(() => {
    if (phase !== 'rest') return undefined;
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, [phase]);

  // Auto-advance when the rest runs out
  useEffect(() => {
    if (phase === 'rest' && restEndsAt && now >= restEndsAt) {
      advanceAfterRest();
    }
  }, [phase, restEndsAt, now, advanceAfterRest]);

  const speechFor = (setIdx) =>
    buildExerciseSpeech({
      currentSet: allSets[setIdx],
      currentSetIdx: setIdx,
      isLastSet: setIdx === allSets.length - 1,
      isPt,
      voiceInstructions: workoutData?.voiceInstructions,
    });

  const pickTip = () => {
    const tips = t('pages.WorkoutExecution.motivationalTips', { returnObjects: true });
    return tips[Math.floor(Math.random() * tips.length)];
  };

  // Narrate when entering exercise phase, and make the line the coming rest will say
  useEffect(() => {
    if (phase !== 'exercise' || !currentSet || isMuted) return;

    speak(speechFor(currentSetIdx), langCode);
    nextTipRef.current = pickTip();
    if (isLastSet) prepareSpeech(COMPLETE_SPEECH[isPt ? 'pt' : 'en'], langCode);
    else if (workoutData?.motivation !== false) prepareSpeech(nextTipRef.current, langCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, currentSetIdx]);

  // Narrate motivational tip when entering rest phase
  useEffect(() => {
    if (phase !== 'rest' || isMuted || workoutData?.motivation === false) return;

    speak(nextTipRef.current || pickTip(), langCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, currentSetIdx]);

  // During the rest, make the next set's line, so it plays the moment the rest ends
  useEffect(() => {
    if (phase !== 'rest' || isMuted || !nextSet) return;

    prepareSpeech(speechFor(currentSetIdx + 1), langCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, currentSetIdx]);

  // Narrate workout completion
  useEffect(() => {
    if (phase !== 'complete' || isMuted) return;

    speak(COMPLETE_SPEECH[isPt ? 'pt' : 'en'], langCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Write back the reps/weights used today so the next session starts from them (any plan)
  const writeBackLastValues = async () => {
    if (Object.keys(setOverrides).length === 0) return;
    const updatedExercises = workoutData.exercises.map((exercise, exerciseIdx) => {
      const seriesCount = parseInt(exercise.series) || 1;
      // Scan all sets to find the last overridden value for each field
      let lastReps = null;
      let lastWeight = null;
      let lastWeightUnit = null;
      for (let s = 1; s <= seriesCount; s++) {
        const r = getSetValue(exerciseIdx, s, 'reps', null);
        const w = getSetValue(exerciseIdx, s, 'weight', null);
        const wu = getSetValue(exerciseIdx, s, 'weightUnit', null);
        if (r !== null) lastReps = r;
        if (w !== null) lastWeight = w;
        if (wu !== null) lastWeightUnit = wu;
      }
      return {
        ...exercise,
        ...(lastReps !== null && { reps: lastReps }),
        ...(lastWeight !== null && { weight: lastWeight }),
        ...(lastWeightUnit !== null && { weightUnit: lastWeightUnit }),
      };
    });

    // Build a clean input — omit Strapi metadata fields that break the mutation
    await updateWorkoutMutation({
      variables: {
        workoutInput: {
          name: workoutData.name,
          description: workoutData.description,
          image: workoutData.image,
          motivation: workoutData.motivation,
          voiceInstructions: workoutData.voiceInstructions ?? true,
          exercises: updatedExercises,
        },
        workoutId,
      },
    });
    await refetchWorkouts();
  };

  // What was done in each set, as the log stores it
  const buildLogExercises = () =>
    workoutData.exercises.map((exercise, exerciseIdx) => {
      const seriesCount = parseInt(exercise.series) || 1;
      return {
        name: exercise.name,
        // The API keeps no copy of the workout's name with a log: each entry carries it, so Progresso shows the
        // session as it was called that day, after the workout is renamed or deleted too
        workoutName: workoutData.name || null,
        workoutDescription: workoutData.description || null,
        exerciseId: exercise.exerciseId || null,
        videoUrl: exercise.videoUrl || null,
        thumbnailUrl: exercise.thumbnailUrl || null,
        gifUrl: exercise.gifUrl || null,
        sets: Array.from({ length: seriesCount }, (_, i) => ({
          setNumber: i + 1,
          weight: getSetValue(exerciseIdx, i + 1, 'weight', exercise.weight || ''),
          weightUnit: getSetValue(exerciseIdx, i + 1, 'weightUnit', exercise.weightUnit || 'kg'),
          reps: getSetValue(exerciseIdx, i + 1, 'reps', exercise.reps || ''),
          completed: true,
        })),
      };
    });

  // The workout history is a Pro feature: the API refuses logs from free accounts
  const saveWorkoutLog = async () => {
    if (!workoutData?.exercises) return;
    if (!isPro) {
      setLogStatus('pro');
      return;
    }

    setLogStatus('saving');
    try {
      const exercises = buildLogExercises();

      await createWorkoutLogMutation({
        variables: {
          workoutLogInput: {
            workout: workoutId,
            completedAt: new Date().toISOString(),
            duration: durationSeconds || null,
            exercises,
          },
        },
      });

      setLogStatus('saved');
      trackEvent('workout_execution_log_saved', 'Workout Execution - Log Saved', 'workout_execution', true);
    } catch {
      setLogStatus('error');
    }
  };

  useEffect(() => {
    if (phase !== 'complete') return;
    // Compared before this session's log is saved, so it only meets earlier sessions
    if (isPro && workoutData?.exercises && dataLogs?.workoutLogs?.data) {
      setNewRecords(findNewRecords(dataLogs.workoutLogs.data, buildLogExercises()));
    }
    writeBackLastValues().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Save when the session ends (and again on retry), once we know whether the account is Pro
  useEffect(() => {
    if (phase === 'complete' && logStatus === 'idle' && me) {
      saveWorkoutLog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, logStatus, me]);

  const beginSession = () => {
    if (startSource) trackEvent(`${startSource}_start_site`, `${startSource} - Start guided workout: site`, startSource, true);
    trackEvent('workout_execution_start', `Workout Execution - Start: ${workoutData?.description}`, 'workout_execution', true);
    startTimeRef.current = Date.now();
    setPhase('exercise');
  };

  // In Portuguese the workout speaks with its own voice: the first workout asks before downloading it (some people
  // train on mobile data), then shows a progress bar; later ones only load it. Any failure starts the workout with
  // the device voice.
  const handleStart = async () => {
    if (isMuted || !canUseVoice(language)) {
      beginSession();
      return;
    }
    unlockVoiceAudio();
    if (isVoiceReady()) {
      beginSession();
      return;
    }

    const missing = await voiceDownloadBytes();
    if (missing > 0) {
      setVoiceDownloadSize(missing);
      setVoiceStatus('confirm');
      return;
    }
    loadVoice();
  };

  const handleDownloadVoice = () => {
    trackEvent('workout_execution_download_voice', 'Workout Execution - Download Voice', 'workout_execution', true);
    unlockVoiceAudio();
    loadVoice();
  };

  const loadVoice = async () => {
    const controller = new AbortController();
    voiceAbortRef.current = controller;
    setVoiceStatus('preparing');
    try {
      await prepareVoice({
        signal: controller.signal,
        onProgress: (progress) => {
          setVoiceStatus('downloading');
          setVoiceProgress(progress);
        },
      });
      setVoiceStatus('preparing');
      // The first set's line, so the workout opens talking
      await prepareLine(speakablePt(speechFor(0)));
    } catch {
      // the device voice takes over
    }
    if (controller.signal.aborted) return;
    voiceAbortRef.current = null;
    setVoiceStatus('idle');
    beginSession();
  };

  // "Start with the device voice", asked or mid-download: stops the download (what finished stays) and starts now
  const handleSkipVoice = () => {
    trackEvent('workout_execution_skip_voice', 'Workout Execution - Skip Voice Download', 'workout_execution', true);
    voiceAbortRef.current?.abort();
    voiceAbortRef.current = null;
    setVoiceStatus('idle');
    beginSession();
  };

  // The other way to train, offered on the opening: the Alexa's how-to page (/sync)
  const handleAlexa = () => {
    const source = startSource || 'workout_execution';
    trackEvent(`${source}_start_alexa`, `${source} - Start guided workout: alexa`, source, true);
  };

  // The current set's load, typed or stepped, and what it carries forward
  const currentSetLoad = () => {
    const { exerciseIdx, setNumber, exercise } = currentSet;
    return getSetValue(exerciseIdx, setNumber, 'weight', exercise.weight || '');
  };

  const handleStep = (field, direction) => {
    const { exerciseIdx, setNumber, exercise } = currentSet;
    if (field === 'reps') {
      const reps = parseInt(getSetValue(exerciseIdx, setNumber, 'reps', exercise.reps || '')) || 0;
      updateSetOverride(exerciseIdx, setNumber, 'reps', String(Math.max(0, reps + direction)));
      return;
    }
    const load = stepLoad(currentSetLoad(), direction * entries[exerciseIdx].loadStep);
    updateSetOverride(exerciseIdx, setNumber, 'weight', load);
    propagateWeightForward(exerciseIdx, setNumber, load);
  };

  const handleCompleteSet = () => {
    cancelSpeech();
    // A load typed and never blurred (the button took the tap) still carries forward
    if (setOverrides[getSetKey(currentSet.exerciseIdx, currentSet.setNumber)]?.weightTyped) {
      propagateWeightForward(currentSet.exerciseIdx, currentSet.setNumber, currentSetLoad());
    }
    trackEvent('workout_execution_complete_set', 'Workout Execution - Complete Set', 'workout_execution', true);
    if (isLastSet) {
      trackEvent('workout_execution_finished', `Workout Execution - Finished: ${workoutData?.description}`, 'workout_execution', true);
      finishSession();
    } else {
      const interval = currentSet.exercise.interval || 60;
      setRestTotal(interval);
      setNow(Date.now());
      setRestEndsAt(Date.now() + interval * 1000);
      setPhase('rest');
    }
  };

  const handleSkipRest = () => {
    trackEvent('workout_execution_skip_rest', 'Workout Execution - Skip Rest', 'workout_execution', true);
    cancelSpeech();
    advanceAfterRest();
  };

  const shiftRest = (seconds) => {
    const current = Date.now();
    setNow(current);
    setRestEndsAt((endsAt) => Math.max(current, (endsAt || current) + seconds * 1000));
    if (seconds > 0) setRestTotal((total) => Math.max(total, restTimeLeft + seconds));
  };

  const handleToggleMute = () => {
    if (!isMuted) cancelSpeech();
    setIsMuted((prev) => !prev);
  };

  useEffect(() => {
    setSpeechVolume(voiceGain(voiceLevel));
  }, [voiceLevel]);

  // A preview still waiting when the set ends doesn't talk over the rest
  useEffect(() => () => clearTimeout(volumePreviewRef.current), [phase, currentSetIdx]);

  // Moving the volume unmutes; once the slider rests, a line already made plays at the new volume: the current set's,
  // or during the rest (the bar stays in view now) the next set's
  const handleVoiceLevel = (level) => {
    setVoiceLevel(level);
    saveVoiceLevel(level);
    setIsMuted(false);
    clearTimeout(volumePreviewRef.current);
    const lineIdx = phase === 'rest' && nextSet ? currentSetIdx + 1 : currentSetIdx;
    volumePreviewRef.current = setTimeout(() => {
      if (level > 0) speak(speechFor(lineIdx), langCode);
    }, 450);
  };

  const workoutTitle = `${t('pages.Dashboard.home.workoutPrefix')} ${workoutData?.name ?? ''}`.trim();

  if (isLoadingWorkouts) {
    return (
      <div className="mt-root mt-root--app mt-guide-page" aria-busy="true">
        <div className="mt-guide-skeleton" />
      </div>
    );
  }

  if (!workoutData) return null;

  if (phase === 'intro') {
    return (
      <div className="mt-root mt-root--app mt-guide-page">
        <SessionIntro
          title={workoutTitle}
          description={workoutData.description}
          entries={entries}
          setCount={allSets.length}
          minutes={estimateWorkoutMinutes(workoutData.exercises)}
          onStart={handleStart}
          onAlexa={handleAlexa}
          voice={{ status: voiceStatus, progress: voiceProgress, downloadSize: voiceDownloadSize }}
          onDownloadVoice={handleDownloadVoice}
          onSkipVoice={handleSkipVoice}
          showSilentHint={isAppleMobile()}
        />
      </div>
    );
  }

  if (phase === 'complete') {
    return (
      <div className="mt-root mt-root--app mt-guide-page">
        <SessionComplete
          workoutTitle={workoutTitle}
          description={workoutData.description}
          minutes={Math.max(1, Math.round(durationSeconds / 60))}
          setCount={allSets.length}
          exerciseCount={workoutData.exercises.length}
          logStatus={logStatus}
          records={newRecords}
          isPt={isPt}
          onRetry={() => setLogStatus('idle')}
          onUpgrade={() => navigate('/plans')}
          onViewProgress={() => navigate('/progress')}
          onBack={() => navigate('/workouts')}
        />
      </div>
    );
  }

  const { exerciseIdx, setNumber } = currentSet;
  const entry = entries[exerciseIdx];
  const repsOf = (idx, n) => getSetValue(idx, n, 'reps', entries[idx].exercise.reps || '');
  const loadOf = (idx, n) => getSetValue(idx, n, 'weight', entries[idx].exercise.weight || '');
  // "8 reps · 50 kg": a set's dose as it stands now (a load carried from an earlier set included)
  const setDose = (idx, n) => {
    const reps = repsOf(idx, n);
    const load = loadOf(idx, n);
    return load
      ? t('pages.WorkoutExecution.session.setDoseLoad', { reps, load: `${formatNumber(load, isPt)} ${entries[idx].unit}` })
      : t('pages.WorkoutExecution.session.setDose', { reps });
  };

  // What comes after this set (under the button) and, during the rest, the set the rest leads to
  const nextIsSameExercise = nextSet?.exerciseIdx === exerciseIdx;
  const after = isLastSet
    ? { kind: 'last' }
    : nextIsSameExercise
      ? { kind: 'set', setNumber: nextSet.setNumber, dose: setDose(exerciseIdx, nextSet.setNumber) }
      : { kind: 'exercise', entry: entries[nextSet.exerciseIdx] };
  const next = nextSet && {
    entry: entries[nextSet.exerciseIdx],
    setNumber: nextSet.setNumber,
    sameExercise: nextIsSameExercise,
    dose: setDose(nextSet.exerciseIdx, nextSet.setNumber),
  };

  // The progress bar's segments: one per exercise, as long as its sets, filled by the ones done
  const segments = entries.map((item) => {
    const first = allSets.findIndex((set) => set.exerciseIdx === item.index);
    return { index: item.index, sets: item.totalSets, done: Math.min(item.totalSets, Math.max(0, completedSetsCount - first)) };
  });

  return (
    <div className="mt-root mt-root--app mt-guide-page">
      <LeaveSessionDialog
        isOpen={blocker.state === 'blocked'}
        onStay={() => blocker.reset?.()}
        onLeave={() => {
          cancelSpeech();
          blocker.proceed?.();
        }}
      />
      <SessionActive
        workoutTitle={workoutTitle}
        doneSets={completedSetsCount}
        totalSets={allSets.length}
        segments={segments}
        entries={entries}
        phase={phase}
        current={{ entry, setNumber, reps: repsOf(exerciseIdx, setNumber), weight: loadOf(exerciseIdx, setNumber) }}
        after={after}
        next={next}
        upNext={entries.slice(exerciseIdx + 1, exerciseIdx + 3)}
        restTimeLeft={restTimeLeft}
        restTotal={restTotal}
        completeLabel={isLastSet ? t('pages.WorkoutExecution.session.finish') : t('pages.WorkoutExecution.session.completeSet', { n: setNumber })}
        isMuted={isMuted}
        voiceLevel={voiceLevel}
        isPt={isPt}
        onClose={() => navigate('/workouts')}
        onToggleMute={handleToggleMute}
        onVoiceLevelChange={handleVoiceLevel}
        onRepsChange={(value) => updateSetOverride(exerciseIdx, setNumber, 'reps', value.replace(/\D/g, ''))}
        onWeightChange={(value) => updateSetOverride(exerciseIdx, setNumber, 'weight', normalizeLoad(value))}
        onWeightCommit={() => {
          if (setOverrides[getSetKey(exerciseIdx, setNumber)]?.weightTyped) propagateWeightForward(exerciseIdx, setNumber, currentSetLoad());
        }}
        onStep={handleStep}
        onCompleteSet={handleCompleteSet}
        onMinus={() => shiftRest(-15)}
        onPlus={() => shiftRest(15)}
        onSkip={handleSkipRest}
      />
    </div>
  );
};
